import { formatMoney } from "@bl/domain";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { FileUp, Plus, Trash2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  ApiError,
  type CatalogProduct,
  type InvoiceSummary,
  newKey,
  parseInvoiceFile,
  send,
  uploadInvoiceFile,
  useCatalogSuppliers,
  useChange,
  useInvoices,
  useSuppliers,
} from "../api";
import {
  Dialog,
  DialogForm,
  Empty,
  ErrorNote,
  Field,
  Toolbar,
  ToolbarButton,
  usePageTitle,
} from "../components/ui";
import { showDate, today } from "../format";
import {
  blankLine,
  type LineDraft,
  LineEntry,
  noSupplier,
  type OfferedSku,
  resolveSupplier,
  type SupplierChoice,
  SupplierField,
  suppliersOnRecord,
} from "../forms/InvoiceEntry";
import { useNotify } from "../notify";

/**
 * Invoices waiting to be received, and the ones already received.
 *
 * A document is read into a draft where a template knows the supplier, and entered by hand where one
 * does not. Entering by hand is not a stopgap: it is a permanent path for breweries that never send
 * us a PDF at all (Tyler, 2026-09-19), and it is the same review screen either way.
 */

export function InvoicesPage() {
  usePageTitle("Invoices");
  const { data: invoices = [], isLoading, error } = useInvoices();
  const [starting, setStarting] = useState(false);
  const [reading, setReading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const notify = useNotify();
  const picker = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const drafts = invoices.filter((i) => i.status === "draft");
  const done = invoices.filter((i) => i.status === "committed");

  const read = async (file: File) => {
    setReading(true);
    setUploadError(null);
    try {
      const result = await parseInvoiceFile(file, newKey());
      if (result.invoice) {
        await queryClient.invalidateQueries();
        navigate({ to: "/invoices/$invoiceId", params: { invoiceId: result.invoice.id } });
        return;
      }
      // Kept, but unread: typing it in beside the document is still the fastest way in.
      notify({
        level: "warning",
        title: "Nothing here reads that document",
        body: `${result.reason ?? ""} The document is kept; enter the lines beside it.`.trim(),
      });
      setStarting(true);
    } catch (e) {
      setUploadError(e instanceof ApiError ? e.message : "That file could not be read");
    } finally {
      setReading(false);
    }
  };

  return (
    <>
      <input
        ref={picker}
        type="file"
        className="sr-only"
        accept="application/pdf,image/png,image/jpeg,text/plain"
        onChange={(e) => {
          const chosen = e.target.files?.[0];
          e.target.value = "";
          if (chosen) void read(chosen);
        }}
      />
      <Toolbar
        left={
          <>
            <ToolbarButton
              icon={<FileUp size={20} />}
              label={reading ? "Reading" : "Import"}
              title="Read an invoice out of a PDF or photo"
              disabled={reading}
              onClick={() => picker.current?.click()}
            />
            <ToolbarButton
              icon={<Plus size={20} />}
              label="By hand"
              title="Enter an invoice by hand"
              onClick={() => setStarting(true)}
            />
          </>
        }
      />
      <div className="page-body stack">
        <h1 className="sr-only">Invoices</h1>
        <ErrorNote error={error} />
        {uploadError ? (
          <div className="form-error" role="alert">
            {uploadError}
          </div>
        ) : null}
        {!isLoading && invoices.length === 0 ? (
          <Empty title="No invoices yet">
            <p className="muted">
              Upload the PDF and we read it, or enter one by hand. Either way the document stays
              with every lot it created.
            </p>
          </Empty>
        ) : null}
        {drafts.length > 0 ? <InvoiceTable title="Waiting to be received" rows={drafts} /> : null}
        {done.length > 0 ? <InvoiceTable title="Received" rows={done} /> : null}
      </div>
      <NewInvoiceDialog open={starting} onClose={() => setStarting(false)} />
    </>
  );
}

function InvoiceTable({ title, rows }: { title: string; rows: InvoiceSummary[] }) {
  return (
    <section className="section">
      <div className="section-header" style={{ cursor: "default" }}>
        <h2>{title}</h2>
        <span className="aside">{rows.length}</span>
      </div>
      <div className="section-body">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Invoice no.</th>
                <th>Date</th>
                <th className="num">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link to="/invoices/$invoiceId" params={{ invoiceId: row.id }}>
                      {row.supplierName ?? "Unnamed supplier"}
                    </Link>
                  </td>
                  <td>{row.invoiceNumber ?? ""}</td>
                  <td>{showDate(row.invoiceDate)}</td>
                  <td className="num">{row.printed.total ? formatMoney(row.printed.total) : ""}</td>
                  <td className="num">
                    {row.reversed ? <span className="chip">put back</span> : null}
                    {row.discrepancies.length > 0 ? (
                      <span className="chip warn">{row.discrepancies.length} to check</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

interface ChargeDraft {
  key: string;
  label: string;
  amount: string;
}

const blankCharge = (label = ""): ChargeDraft => ({
  key: newKey(),
  label,
  amount: "",
});

/** An item number as typed or printed, for comparing: "gw dext" and "GW-DEXT" are the same. */
const skuKey = (sku: string) => sku.toUpperCase().replace(/[^A-Z0-9]/g, "");

export function NewInvoiceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: suppliers = [] } = useSuppliers();
  const { data: sellers = [] } = useCatalogSuppliers();
  const onRecord = useMemo(() => suppliersOnRecord(suppliers, sellers), [suppliers, sellers]);
  const navigate = useNavigate();
  const notify = useNotify();
  const fileInput = useRef<HTMLInputElement>(null);

  const [supplier, setSupplier] = useState<SupplierChoice>(noSupplier);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [file, setFile] = useState<File | null>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [charges, setCharges] = useState<ChargeDraft[]>([blankCharge("Freight")]);

  const create = useChange(send.invoice, {
    onSuccess: (invoice) => {
      notify({ title: "Draft started", link: { kind: "invoice", invoiceId: invoice.id } });
      close();
      navigate({ to: "/invoices/$invoiceId", params: { invoiceId: invoice.id } });
    },
  });

  const close = () => {
    setSupplier(noSupplier);
    setInvoiceNumber("");
    setInvoiceDate(today());
    setFile(null);
    setFileId(null);
    setUploadError(null);
    setLines([blankLine()]);
    setCharges([blankCharge("Freight")]);
    create.reset();
    onClose();
  };

  const attach = async (chosen: File) => {
    setUploadError(null);
    setFile(chosen);
    try {
      const result = await uploadInvoiceFile(chosen, newKey());
      setFileId(result.fileId);
      if (result.existingInvoiceId) {
        setUploadError("This exact document is already on another invoice here.");
      }
    } catch (error) {
      setFile(null);
      setUploadError(error instanceof ApiError ? error.message : "That file could not be stored");
    }
  };

  const setLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  // Changing what a picked line says it is lets go of the pick: it may not be that item any more.
  // Its quantity and price are only being filled in.
  const editLine = (key: string, patch: Partial<LineDraft>) =>
    setLine(
      key,
      "supplierSku" in patch || "description" in patch
        ? { ...patch, itemId: undefined, pickedName: undefined }
        : patch,
    );

  const resolved = resolveSupplier(supplier, onRecord);

  /**
   * The SKU a catalog product would fill in, and whose it is.
   *
   * Searched from the SKU box, it is the item number that matches what was typed: the product was
   * found by it, so that is the one meant, whichever seller it is and however many sizes the product
   * comes in (Tyler, 2026-09-28). The chosen supplier's own comes first where two match.
   *
   * Searched by description, it is the chosen supplier's own, where the catalog knows whose they are.
   * With no supplier said, a product only one seller offers says whose it is; one several sell leaves
   * the SKU alone rather than guess. A supplier the catalog does not know has SKUs of its own, so none
   * is filled for it.
   */
  const skuFor = (product: CatalogProduct, typedSku: string | null): OfferedSku | null => {
    const sold = product.offerings.flatMap((o) =>
      o.sku ? [{ sku: o.sku, supplier: o.supplier }] : [],
    );
    const typed = skuKey(typedSku ?? "");
    if (typed) {
      const theirsFirst = [...sold].sort(
        (a, b) =>
          Number(b.supplier.id === resolved.companyId) -
          Number(a.supplier.id === resolved.companyId),
      );
      const hit =
        theirsFirst.find((o) => skuKey(o.sku) === typed) ??
        theirsFirst.find((o) => skuKey(o.sku).startsWith(typed)) ??
        theirsFirst.find((o) => skuKey(o.sku).includes(typed));
      if (hit) return hit;
    }
    const pickFrom = resolved.companyId
      ? sold.filter((o) => o.supplier.id === resolved.companyId)
      : resolved.said
        ? []
        : sold;
    const sellers = new Set(pickFrom.map((o) => o.supplier.id));
    const [first] = pickFrom;
    return first && sellers.size === 1 ? first : null;
  };

  const sellsIt = resolved.companyId
    ? (product: CatalogProduct) =>
        product.offerings.some((o) => o.supplier.id === resolved.companyId)
    : null;

  const pickProduct = (line: LineDraft, product: CatalogProduct, typedSku: string | null) => {
    const offered = skuFor(product, typedSku);
    if (offered && !resolved.said) {
      const ours = suppliers.find((s) => s.catalogCompanyId === offered.supplier.id);
      setSupplier(
        ours
          ? { supplierId: ours.id, other: null }
          : { supplierId: "", other: offered.supplier.name },
      );
    }
    setLine(line.key, {
      description: product.name,
      supplierSku: offered?.sku ?? line.supplierSku,
      itemId: product.adoptedItemIds[0],
      pickedName: product.adoptedItemIds.length ? product.name : undefined,
    });
  };

  const ready = lines.some((l) => l.description.trim() && l.billedUnits.trim());

  const submit = () => {
    create.mutate({
      origin: file ? "upload" : "manual",
      supplierId: resolved.supplierId,
      supplierName: resolved.supplierName,
      invoiceNumber: invoiceNumber.trim() || undefined,
      invoiceDate: invoiceDate || undefined,
      fileId: fileId ?? undefined,
      lines: lines
        .filter((l) => l.description.trim() && l.billedUnits.trim())
        .map((l) => ({
          supplierSku: l.supplierSku.trim() || undefined,
          description: l.description.trim(),
          billedUnits: l.billedUnits.trim(),
          unitPrice: l.unitPrice.trim() || undefined,
          extendedPrice: l.extendedPrice.trim() || undefined,
          itemId: l.itemId,
        })),
      charges: charges
        .filter((c) => c.label.trim() && c.amount.trim())
        .map((c) => ({ label: c.label.trim(), kind: "freight" as const, amount: c.amount.trim() })),
    });
  };

  return (
    <Dialog open={open} onClose={close} title="Enter an invoice" wide>
      <DialogForm
        onSubmit={submit}
        footer={
          <>
            <button type="button" className="button" onClick={close}>
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={!ready || create.isPending}>
              {create.isPending ? "Starting" : "Start the review"}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <Field label="Supplier">
            <SupplierField
              ours={suppliers}
              onRecord={onRecord}
              choice={supplier}
              onChange={setSupplier}
            />
          </Field>
          <Field label="Invoice no.">
            <input
              className="input"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </Field>
          <Field label="Invoice date">
            <input
              className="input"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </Field>
        </div>

        <div className="stack">
          <input
            ref={fileInput}
            type="file"
            className="sr-only"
            accept="application/pdf,image/png,image/jpeg,text/plain"
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              if (chosen) void attach(chosen);
            }}
          />
          <div className="row">
            <button type="button" className="button" onClick={() => fileInput.current?.click()}>
              <FileUp size={16} /> {file ? "Choose another" : "Attach the document"}
            </button>
            {file ? <span className="muted">{file.name}</span> : null}
          </div>
          {uploadError ? (
            <div className="form-error" role="alert">
              {uploadError}
            </div>
          ) : null}
        </div>

        <div className="section-header" style={{ cursor: "default", paddingLeft: 0 }}>
          <h2>Line items</h2>
        </div>
        <div className="stack">
          {lines.map((line) => (
            <LineEntry
              key={line.key}
              line={line}
              onEdit={(patch) => editLine(line.key, patch)}
              onPick={(product, typedSku) => pickProduct(line, product, typedSku)}
              skuFor={skuFor}
              sellsIt={sellsIt}
              onRemove={() => setLines((all) => all.filter((l) => l.key !== line.key))}
              canRemove={lines.length > 1}
            />
          ))}
          <div className="row">
            <button
              type="button"
              className="button"
              onClick={() => setLines((all) => [...all, blankLine()])}
            >
              <Plus size={16} /> Another line
            </button>
          </div>
        </div>

        <div className="section-header" style={{ cursor: "default", paddingLeft: 0 }}>
          <h2>Freight and fees</h2>
        </div>
        <div className="stack">
          {charges.map((charge) => (
            <div className="entry-row charges" key={charge.key}>
              <input
                className="input"
                placeholder="Freight, Skid, FSC"
                aria-label="Charge"
                value={charge.label}
                onChange={(e) =>
                  setCharges((all) =>
                    all.map((c) => (c.key === charge.key ? { ...c, label: e.target.value } : c)),
                  )
                }
              />
              <input
                className="input num"
                placeholder="Amount"
                aria-label="Charge amount"
                inputMode="decimal"
                value={charge.amount}
                onChange={(e) =>
                  setCharges((all) =>
                    all.map((c) => (c.key === charge.key ? { ...c, amount: e.target.value } : c)),
                  )
                }
              />
              <button
                type="button"
                className="icon-button"
                aria-label="Remove this charge"
                onClick={() => setCharges((all) => all.filter((c) => c.key !== charge.key))}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <div className="row">
            <button
              type="button"
              className="button"
              onClick={() => setCharges((all) => [...all, blankCharge()])}
            >
              <Plus size={16} /> Another charge
            </button>
          </div>
        </div>
        <ErrorNote error={create.error} />
      </DialogForm>
    </Dialog>
  );
}
